import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Sidebar from '../../components/shared/Sidebar';
import { FiArrowLeft, FiEdit, FiRefreshCw, FiDollarSign, FiLock } from 'react-icons/fi';

export default function AdminShopDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subModal, setSubModal] = useState(false);
  const [payModal, setPayModal] = useState(false);
  const [passModal, setPassModal] = useState(false);
  const [subForm, setSubForm] = useState({ plan_id: '', billing_cycle: 'monthly', extend_days: '', new_end_date: '', subscription_status: '' });
  const [payForm, setPayForm] = useState({ plan_id: '', billing_cycle: 'monthly', amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'Cash', transaction_id: '', period_start: '', period_end: '', notes: '' });
  const [newPass, setNewPass] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([axios.get(`/api/admin/shops/${id}`), axios.get('/api/admin/plans')])
      .then(([shopRes, plansRes]) => { setData(shopRes.data); setPlans(plansRes.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const toggleStatus = async () => {
    if (!window.confirm(`Shop ko ${data.shop.is_active ? 'suspend' : 'activate'} karna chahte hain?`)) return;
    try { const r = await axios.patch(`/api/admin/shops/${id}/toggle`); toast.success(r.data.message); load(); }
    catch { toast.error('Error'); }
  };

  const togglePublic = async () => {
    try { const r = await axios.patch(`/api/admin/shops/${id}/toggle-public`); toast.success(r.data.message); load(); }
    catch { toast.error('Error'); }
  };

  const copyShopLink = () => {
    const link = `${window.location.origin}/shop/${data.shop.slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copy ho gaya!');
  };

  const handleSubUpdate = async (e) => {
    e.preventDefault();
    try { await axios.patch(`/api/admin/shops/${id}/subscription`, subForm); toast.success('Subscription update ho gayi!'); setSubModal(false); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try { await axios.post('/api/admin/payments', { ...payForm, shop_id: id }); toast.success('Payment record ho gayi!'); setPayModal(false); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleResetPass = async (e) => {
    e.preventDefault();
    try { await axios.post(`/api/admin/shops/${id}/reset-password`, { newPassword: newPass }); toast.success('Password reset ho gaya!'); setPassModal(false); setNewPass(''); }
    catch { toast.error('Error'); }
  };

  if (loading) return <div className="app-layout"><Sidebar /><div className="main-content"><div className="spinner" style={{ margin: '60px auto' }} /></div></div>;
  if (!data) return null;

  const { shop, stats, recentPayments } = data;

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <div className="flex-between page-header">
          <div className="flex gap-3">
            <Link to="/admin/shops" className="btn btn-outline btn-sm"><FiArrowLeft /></Link>
            <div>
              <h1>{shop.shop_name}</h1>
              <p>{shop.email} | {shop.city}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-sm" style={{ background: '#f0f0f0' }} onClick={() => setPassModal(true)}><FiLock /> Reset Password</button>
            <button className="btn btn-sm" style={{ background: '#d1ecf1' }} onClick={copyShopLink}>🔗 Link Copy</button>
            <button className={`btn btn-sm ${shop.is_public ? 'btn-success' : 'btn-outline'}`} onClick={togglePublic}>
              {shop.is_public ? '🌐 Public' : '🔒 Private'}
            </button>
            <button className={`btn btn-sm ${shop.is_active ? 'btn-danger' : 'btn-success'}`} onClick={toggleStatus}>
              {shop.is_active ? '🚫 Suspend' : '✅ Activate'}
            </button>
          </div>
        </div>

        <div className="grid-2" style={{ marginBottom: '24px' }}>
          {/* Shop Info */}
          <div className="card">
            <h3 style={{ marginBottom: '16px' }}>📋 Shop Details</h3>
            {[['Owner', shop.owner_name], ['Phone', shop.phone], ['WhatsApp', shop.whatsapp_number], ['City', shop.city], ['Address', shop.address]].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '14px' }}>
                <span style={{ color: 'var(--text-light)' }}>{l}</span>
                <span style={{ fontWeight: 500 }}>{v || '—'}</span>
              </div>
            ))}
          </div>

          {/* Subscription */}
          <div className="card" style={{ borderTop: `4px solid ${shop.subscription_status === 'active' ? 'var(--success)' : 'var(--danger)'}` }}>
            <div className="flex-between mb-3">
              <h3>💳 Subscription</h3>
              <span className={`badge ${shop.subscription_status === 'active' ? 'badge-success' : shop.subscription_status === 'expired' ? 'badge-warning' : 'badge-danger'}`}>{shop.subscription_status}</span>
            </div>
            {[['Plan', shop.plan_name || 'None'], ['Billing', shop.billing_cycle], ['Start', shop.subscription_start ? new Date(shop.subscription_start).toLocaleDateString('en-PK') : '—'], ['Expires', shop.subscription_end ? new Date(shop.subscription_end).toLocaleDateString('en-PK') : '—']].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '14px' }}>
                <span style={{ color: 'var(--text-light)' }}>{l}</span>
                <span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ))}
            <div className="flex gap-2 mt-4">
              <button className="btn btn-primary btn-sm" onClick={() => { setSubForm({ plan_id: shop.plan_id || '', billing_cycle: shop.billing_cycle || 'monthly', extend_days: '', new_end_date: '', subscription_status: shop.subscription_status }); setSubModal(true); }}>
                <FiRefreshCw /> Subscription Edit
              </button>
              <button className="btn btn-success btn-sm" onClick={() => { setPayForm(f => ({ ...f, plan_id: shop.plan_id || '', billing_cycle: shop.billing_cycle || 'monthly' })); setPayModal(true); }}>
                <FiDollarSign /> Payment Record
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid-3 mb-4">
          {[['Total Bookings', stats.total || 0], ['Completed', stats.completed || 0], ['Pending', stats.pending || 0]].map(([l, v]) => (
            <div key={l} className="stat-card"><div className="stat-value">{v}</div><div className="stat-label">{l}</div></div>
          ))}
        </div>

        {/* Payment History */}
        {recentPayments.length > 0 && (
          <div className="card">
            <h3 style={{ marginBottom: '16px' }}>💰 Recent Payments</h3>
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Date</th><th>Amount</th><th>Method</th><th>Period</th><th>Notes</th></tr></thead>
                <tbody>
                  {recentPayments.map(p => (
                    <tr key={p.id}>
                      <td>{new Date(p.payment_date).toLocaleDateString('en-PK')}</td>
                      <td><strong>PKR {Number(p.amount).toLocaleString()}</strong></td>
                      <td>{p.payment_method}</td>
                      <td style={{ fontSize: '12px' }}>{new Date(p.period_start).toLocaleDateString('en-PK')} — {new Date(p.period_end).toLocaleDateString('en-PK')}</td>
                      <td style={{ fontSize: '12px', color: 'var(--text-light)' }}>{p.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Subscription Modal */}
        {subModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSubModal(false)}>
            <div className="modal">
              <div className="modal-header"><h3>💳 Subscription Update</h3><button className="modal-close" onClick={() => setSubModal(false)}>×</button></div>
              <form onSubmit={handleSubUpdate}>
                <div className="form-group">
                  <label className="form-label">Plan Change Karein</label>
                  <select className="form-control" value={subForm.plan_id} onChange={e => setSubForm(f => ({...f, plan_id: e.target.value}))}>
                    <option value="">— Plan Select Karein —</option>
                    {plans.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Billing Cycle</label>
                  <select className="form-control" value={subForm.billing_cycle} onChange={e => setSubForm(f => ({...f, billing_cycle: e.target.value}))}>
                    <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status Change Karein</label>
                  <select className="form-control" value={subForm.subscription_status} onChange={e => setSubForm(f => ({...f, subscription_status: e.target.value}))}>
                    <option value="">— Change Na Karein —</option>
                    <option value="active">Active</option><option value="expired">Expired</option><option value="trial">Trial</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Extend (X din aur barhao)</label>
                  <input type="number" className="form-control" placeholder="e.g. 30" value={subForm.extend_days} onChange={e => setSubForm(f => ({...f, extend_days: e.target.value, new_end_date: ''}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ya New End Date Set Karein</label>
                  <input type="date" className="form-control" value={subForm.new_end_date} onChange={e => setSubForm(f => ({...f, new_end_date: e.target.value, extend_days: ''}))} />
                </div>
                <button className="btn btn-primary btn-full" type="submit">💾 Update Karein</button>
              </form>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {payModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPayModal(false)}>
            <div className="modal">
              <div className="modal-header"><h3>💰 Payment Record</h3><button className="modal-close" onClick={() => setPayModal(false)}>×</button></div>
              <form onSubmit={handlePayment}>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Plan</label>
                    <select className="form-control" value={payForm.plan_id} onChange={e => setPayForm(f => ({...f, plan_id: e.target.value}))} required>
                      <option value="">Select Plan</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Billing Cycle</label>
                    <select className="form-control" value={payForm.billing_cycle} onChange={e => setPayForm(f => ({...f, billing_cycle: e.target.value}))}>
                      <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Amount (PKR)</label>
                    <input type="number" className="form-control" value={payForm.amount} onChange={e => setPayForm(f => ({...f, amount: e.target.value}))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Date</label>
                    <input type="date" className="form-control" value={payForm.payment_date} onChange={e => setPayForm(f => ({...f, payment_date: e.target.value}))} required />
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Payment Method</label>
                    <select className="form-control" value={payForm.payment_method} onChange={e => setPayForm(f => ({...f, payment_method: e.target.value}))}>
                      {['Cash', 'JazzCash', 'EasyPaisa', 'Bank Transfer', 'Other'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Transaction ID (optional)</label>
                    <input className="form-control" value={payForm.transaction_id} onChange={e => setPayForm(f => ({...f, transaction_id: e.target.value}))} />
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Period Start</label>
                    <input type="date" className="form-control" value={payForm.period_start} onChange={e => setPayForm(f => ({...f, period_start: e.target.value}))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Period End</label>
                    <input type="date" className="form-control" value={payForm.period_end} onChange={e => setPayForm(f => ({...f, period_end: e.target.value}))} required />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={2} value={payForm.notes} onChange={e => setPayForm(f => ({...f, notes: e.target.value}))} />
                </div>
                <button className="btn btn-success btn-full" type="submit">✅ Payment Record Karein</button>
              </form>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {passModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPassModal(false)}>
            <div className="modal" style={{ maxWidth: '400px' }}>
              <div className="modal-header"><h3>🔑 Password Reset</h3><button className="modal-close" onClick={() => setPassModal(false)}>×</button></div>
              <form onSubmit={handleResetPass}>
                <div className="form-group">
                  <label className="form-label">Naya Password</label>
                  <input type="password" className="form-control" value={newPass} onChange={e => setNewPass(e.target.value)} minLength={6} required placeholder="Min 6 characters" />
                </div>
                <button className="btn btn-primary btn-full" type="submit">🔑 Password Reset Karein</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
